import React, { Component } from 'react'
import { compose } from 'redux'
import { blur, change, unregisterField, touch } from 'redux-form'
import PropTypes from 'prop-types'
import {
  required as validateRequired,
  getAddressFieldToValidate,
  getWarningValidator
} from '@cred/shared/validations'
import { Label } from '@cred/react-label'
import Font from '@cred/react-font'
import rollbar from '@cred/shared/rollbar'
import { resolve } from '@cred/shared/utils'
import loadGoogleMapsApi from 'load-google-maps-api'
import config from 'config'
import {
  Input,
  Link,
  Dropdown,
  textInputWithAnalytics,
  zipInputWithAnalytics,
  selectCustomWithAnalytics,
  AddressSearch,
  hiddenInput
} from '../components'
import ControlForm from '../components/control-form'
import withForm from '@cred/shared/hoc/with-form'
import styles from './styles.module.scss'
import { states } from '../defaultData'

const addressFields = {
  ADDRESS_SEARCH: 'addressSearch',
  STREET: 'street',
  ZIPCODE: 'zipcode'
}

/**
 * @visibleName Full Address
 */
class FullAddress extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    autoFocus: PropTypes.bool,
    singleLine: PropTypes.bool,
    required: PropTypes.bool,
    analyticsContexts: PropTypes.array,
    formName: PropTypes.string.isRequired,
    warnings: PropTypes.arrayOf(PropTypes.string),
    customInputValidations: PropTypes.shape({
      [addressFields.STREET]: PropTypes.arrayOf(PropTypes.func),
      [addressFields.ZIPCODE]: PropTypes.arrayOf(PropTypes.func)
    }),
    waitGoogleApiAsync: PropTypes.bool
  }

  static defaultProps = {
    autoFocus: false,
    loadingComponent: null,
    autoComplete: 'off',
    singleLine: false,
    required: true,
    onToggleAddress: () => {},
    analyticsContexts: [],
    warnings: [],
    customInputValidations: {
      [addressFields.STREET]: [],
      [addressFields.ZIPCODE]: []
    },
    waitGoogleApiAsync: false
  }

  static displayName = 'FullAddress'

  constructor(props) {
    super(props)

    this.state = {
      manual: false,
      addressSearchWarning: '',
      streetWarning: '',
      googleMapsApiError: false
    }

    this.handleToggleMode = this.handleToggleMode.bind(this)
    this.handleSelectAddress = this.handleSelectAddress.bind(this)
    this.validateAddressSearch = this.validateAddressSearch.bind(this)
    this.handleFullAddressBlur = this.handleFullAddressBlur.bind(this)

    const { name, formData, formName, dispatch, warnings } = props,
      formValues = resolve(`values.${name}`, formData) || resolve(`initial.${name}`, formData)

    if (
      formValues &&
      formValues.street &&
      formValues.city &&
      formValues.state &&
      formValues.zipcode
    ) {
      const fullAddressValue = `${formValues.street}, ${formValues.city}, ${formValues.state} ${formValues.zipcode}`

      dispatch(
        change('control-form', `${formName}.${name}.fullAddress`, fullAddressValue, false, false)
      )
    }

    this.inputValidations = {
      street: warnings.length
        ? this.validateAndSetWarnings(addressFields.STREET)
        : validateRequired,
      zip: warnings.length ? this.validateAndSetWarnings(addressFields.ZIPCODE) : validateRequired
    }
  }

  componentDidMount() {
    loadGoogleMapsApi({
      key: config.googleMapsApiKey,
      libraries: ['places']
    })
      .then(() => {
        this.setState({ googleMapsLoaded: true })
      })
      .catch((error) => {
        this.setState({ googleMapsApiError: true })

        if (this.props.waitGoogleApiAsync) {
          this.setState({ manual: true })
          this.removeFullAddressField()
        }

        rollbar.error('Error loading Google Maps Api', error)
      })
  }

  componentWillUnmount() {
    this.removeFullAddressField()
  }

  render() {
    const {
        name,
        title,
        placeholder,
        className,
        autoFocus,
        loadingComponent,
        autoComplete,
        singleLine,
        required,
        analyticsContexts,
        formName,
        waitGoogleApiAsync
      } = this.props,
      searchGrid = singleLine ? [styles.pureULg23, styles.pureU1].join(' ') : styles.pureU1,
      unitGrid = singleLine ? [styles.pureULg13, styles.pureU1].join(' ') : styles.pureU1,
      { addressSearchWarning, streetWarning, zipcodeWarning, googleMapsApiError } = this.state

    const waitGoogleApiAsyncFlow = waitGoogleApiAsync && googleMapsApiError

    if (this.state.manual) {
      return (
        <div className={className}>
          <Label>{title}</Label>
          <div className={styles.manual}>
            <div className={[styles.pureG, styles.address].join(' ')}>
              <div className={styles.pureU1}>
                <Input
                  analyticsContexts={analyticsContexts}
                  autoFocus={autoFocus}
                  sensitive={true}
                  type="text"
                  label="Address (street)"
                  placeholder="Address (street)"
                  name={`${name}.street`}
                  component={textInputWithAnalytics}
                  validate={required && this.inputValidations.street}
                  warning={streetWarning}
                  className="mt-16"
                />
              </div>
              <div className={styles.pureU1}>
                <Input
                  analyticsContexts={analyticsContexts}
                  type="text"
                  label="Unit (Optional)"
                  placeholder="Unit (Optional)"
                  name={`${name}.unit`}
                  component={textInputWithAnalytics}
                  className="mt-16"
                />
              </div>
              <div className={styles.pureU1}>
                <Input
                  analyticsContexts={analyticsContexts}
                  type="text"
                  label="City"
                  name={`${name}.city`}
                  placeholder="City"
                  component={textInputWithAnalytics}
                  validate={required && validateRequired}
                  className="mt-16"
                />
              </div>
              <div className={styles.pureU1}>
                <Dropdown
                  label="State"
                  placeholder="State"
                  type="text"
                  name={`${name}.state`}
                  options={states}
                  validate={required && validateRequired}
                  component={selectCustomWithAnalytics}
                  analyticsContexts={analyticsContexts}
                  className="dropdown-custom mt-16"
                />
              </div>
              <div className={styles.pureU1}>
                <Input
                  analyticsContexts={analyticsContexts}
                  type="tel"
                  label="ZIP code"
                  name={`${name}.zipcode`}
                  placeholder="ZIP code"
                  component={zipInputWithAnalytics}
                  validate={required && this.inputValidations.zip}
                  warning={zipcodeWarning}
                  className="mt-16"
                />
              </div>
              {waitGoogleApiAsyncFlow ? null : (
                <div className={styles.pureU1}>
                  <Font type="bodySmall" className="mt-16">
                    Switch back to{' '}
                    <Link
                      type="bodySmall"
                      analyticsContexts={analyticsContexts}
                      onClick={this.handleToggleMode}
                    >
                      address search
                    </Link>
                  </Font>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    } else {
      return waitGoogleApiAsyncFlow ? null : (
        <div className={[styles.pureG, styles.address, className].join(' ')}>
          <div className={searchGrid}>
            <ControlForm>
              {this.state.googleMapsLoaded ? (
                <AddressSearch
                  name={`${formName}.${name}.fullAddress`}
                  label={title}
                  placeholder={placeholder}
                  onSelectAddress={this.handleSelectAddress}
                  onBlur={this.handleFullAddressBlur}
                  validate={this.validateAddressSearch}
                  warning={addressSearchWarning}
                  autoComplete={autoComplete}
                  autoFocus={autoFocus}
                  className={singleLine ? 'mr-lg-8' : ''}
                  analyticsContexts={analyticsContexts}
                />
              ) : (
                <div>
                  {waitGoogleApiAsync ? (
                    <Input
                      name={`${formName}.${name}.fullAddress`}
                      label={title}
                      placeholder={placeholder}
                      warning={addressSearchWarning}
                      type="text"
                      component={textInputWithAnalytics}
                      validate={required && validateRequired}
                      analyticsContexts={analyticsContexts}
                    />
                  ) : (
                    <Input
                      component={hiddenInput}
                      name={`${formName}.${name}.fullAddress`}
                      validate={required ? validateRequired : null}
                    />
                  )}
                  {loadingComponent}
                </div>
              )}
            </ControlForm>
            <Font type="bodySmall" className={singleLine ? 'mt-8 mb-16 mb-lg-0' : 'mt-8 mb-16'}>
              Can't find the address?{' '}
              <Link
                type="bodySmall"
                analyticsContexts={analyticsContexts}
                onClick={this.handleToggleMode}
              >
                Enter it manually
              </Link>
            </Font>
          </div>
          <div className={unitGrid}>
            <Input
              type="text"
              label="Unit (Optional)"
              placeholder="Unit (Optional)"
              name={`${name}.unit`}
              component={textInputWithAnalytics}
              className={singleLine ? 'ml-lg-8' : ''}
              analyticsContexts={analyticsContexts}
            />
          </div>
        </div>
      )
    }
  }

  handleSelectAddress(addressData) {
    const { name, dispatch, formName, warnings } = this.props,
      shouldDisplayWarnings = Object.keys(warnings).length

    // Add address name to state for warning validations
    if (shouldDisplayWarnings) dispatch(change(formName, `${name}.name`, addressData.name))
    dispatch(change(formName, `${name}.street`, addressData.street))
    dispatch(change(formName, `${name}.city`, addressData.city))
    dispatch(change(formName, `${name}.state`, addressData.state))
    dispatch(change(formName, `${name}.zipcode`, addressData.zipcode))
    dispatch(change('control-form', `${formName}.${name}.fullAddress`, addressData.fullAddress))
    dispatch(touch(formName, `${name}.street`, `${name}.city`, `${name}.state`, `${name}.zipcode`))
    dispatch(touch('control-form', `${formName}.${name}.fullAddress`))

    this.setState({ lastFullAddress: addressData.fullAddress })

    if (!addressData.complete) {
      alert('Could not find your full address. Please enter details manually')
      this.setState({ manual: true })
    }
  }

  handleFullAddressBlur(e) {
    const { name, dispatch, formName } = this.props

    dispatch(change('control-form', `${formName}.${name}.fullAddress`, this.state.lastFullAddress))
  }

  removeFullAddressField = () => {
    const { dispatch, formName, name } = this.props,
      unregister = unregisterField('control-form', `${formName}.${name}.fullAddress`),
      reset = dispatch(blur('control-form', `${formName}.${name}.fullAddress`, undefined, false))

    unregister.payload.destroyOnUnmount = true

    dispatch(reset)
    dispatch(unregister)
  }

  handleToggleMode() {
    const { onToggleAddress } = this.props

    this.setState({ manual: !this.state.manual })

    if (this.state.manual) {
      this.setState({ lastFullAddress: '' })
    } else {
      this.removeFullAddressField()
    }

    onToggleAddress(!this.state.manual)
  }

  validateAndSetWarnings = (fieldName) => (value) => {
    let error,
      validators = [validateRequired]

    const { customInputValidations } = this.props,
      warningState = { [fieldName]: value }

    if (customInputValidations[fieldName]) {
      validators = [...validators, ...customInputValidations[fieldName]]
    }

    for (const validationFunction of validators) {
      error = validationFunction(value)
      if (error) {
        break
      }
    }

    // We want to pass the component name so setWarnings knows whether
    // to set errors for the address search or one of the address input
    // components (street, zipcode, etc.)
    if (!error) this.setWarnings(warningState, fieldName)

    return error
  }

  getWarningMessage = (values, componentName) => {
    const { warnings } = this.props
    if (!warnings.length || !componentName) return

    let message
    for (let i = 0; i < warnings.length; i++) {
      const warningType = warnings[i],
        validate = getWarningValidator(warningType),
        addressField = getAddressFieldToValidate(warningType, componentName),
        value = values[addressField]

      if (!validate) continue

      message = validate(value)
      // Break out of loop when first warning
      if (message) break
    }

    return message
  }

  resetWarning = (name) => {
    const { [name]: warning } = this.state
    if (warning) this.setState({ [name]: '' })
  }

  getWarningStateKey = (componentName) => {
    const { ADDRESS_SEARCH, STREET, ZIPCODE } = addressFields

    switch (componentName) {
      case ADDRESS_SEARCH:
        return 'addressSearchWarning'
      case STREET:
        return 'streetWarning'
      case ZIPCODE:
        return 'zipcodeWarning'
      default:
        return
    }
  }

  setWarnings = (values, componentName) => {
    const { warnings } = this.props
    if (!warnings.length) return

    const message = this.getWarningMessage(values, componentName)

    // State updates are batched so this will only clear the warning
    // if there is no longer a violation in the address text -- more specifically,
    // the warning message will either be reset or set to a certain value
    const warningStateKey = this.getWarningStateKey(componentName)
    this.resetWarning(warningStateKey)

    if (message) this.setState({ [warningStateKey]: message })
  }

  validateAddressSearch = (value) => {
    const { formData, name, required } = this.props,
      formValues = formData ? formData.values : formData,
      values = formValues ? resolve(name, formValues) : null,
      { ADDRESS_SEARCH } = addressFields

    let error

    if (required) error = validateRequired(value) || validateRequired(values)
    if (values) {
      error =
        validateRequired(values.street) ||
        validateRequired(values.city) ||
        validateRequired(values.state) ||
        validateRequired(values.zipcode)

      if (!error) this.setWarnings(values, ADDRESS_SEARCH)
    }

    return error
  }
}

export { FullAddress as FullAddressNoHoc, addressFields }

export default /*#__PURE__*/ compose(withForm)(FullAddress)
